import { useCallback, useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import { hardRefresh } from '../../../utils/hard-refresh';
import {
  REFRESH_BROADCAST_DONE_TOAST,
  REFRESH_DONE_TOAST,
  REFRESH_INITIATOR_KEY,
  REFRESH_INITIATOR_MAX_AGE_MS,
  REFRESH_MANAGER_DEFERRED_TOAST,
  REFRESH_MANAGER_NOW_TOAST,
  REFRESH_NOTICE_KEY,
  REFRESH_SELF_DEFERRED_TOAST,
  type RefreshNotice,
} from './refresh.toasts';

// Reads (and optionally clears) the fresh initiator marker set by the broadcasting device.
function consumeInitiatorMarker(clear: boolean): boolean {
  try {
    const raw = sessionStorage.getItem(REFRESH_INITIATOR_KEY);
    if (!raw) {
      return false;
    }
    if (clear) {
      sessionStorage.removeItem(REFRESH_INITIATOR_KEY);
    }
    const age = Date.now() - Number(raw);
    return Number.isFinite(age) && age >= 0 && age <= REFRESH_INITIATOR_MAX_AGE_MS;
  } catch {
    return false;
  }
}

export type RefreshSource = 'self' | 'manager';

// A pending refresh fires only after the zone has been continuously safe for this long.
const SAFE_SETTLE_MS = 300;

// Writes the post-reload notice, then hard-refreshes; the marker survives the reload that follows.
function applyRefresh(notice: RefreshNotice): void {
  try {
    sessionStorage.setItem(REFRESH_NOTICE_KEY, notice);
  } catch {
    // sessionStorage may be unavailable (private mode); never block the reload on it.
  }
  void hardRefresh();
}

// Architectural summary: gate; refresh now if safe, else hold pending and auto-apply on return.
export function useDeferredRefresh(isSafeZone: boolean): {
  requestRefresh: (source: RefreshSource) => void;
} {
  const pendingRef = useRef<RefreshSource | null>(null);
  const pendingNoticeRef = useRef<RefreshNotice>('deferred');
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSafeZoneRef = useRef(isSafeZone);
  isSafeZoneRef.current = isSafeZone;

  const clearSettle = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  // Arm the settle countdown; fire the pending refresh after SAFE_SETTLE_MS of continuous safety.
  const armSettle = useCallback(() => {
    if (!isSafeZoneRef.current || pendingRef.current === null) {
      clearSettle();
      return;
    }
    if (settleTimerRef.current !== null) return;
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      // Re-verify safety at expiry: the zone may have flipped unsafe without a re-render.
      if (isSafeZoneRef.current && pendingRef.current !== null) {
        // hardRefresh reloads the page, so no need to clear pendingRef — the tab is replaced.
        applyRefresh(pendingNoticeRef.current);
      }
    }, SAFE_SETTLE_MS);
  }, [clearSettle]);

  const requestRefresh = useCallback((source: RefreshSource) => {
    // The broadcasting device stays silent pre-reload; its single combined toast shows post-reload.
    const isInitiator = source === 'manager' && consumeInitiatorMarker(false);
    // Last requester wins the pending slot; the reload that eventually fires is identical for both.
    pendingRef.current = source;
    if (isSafeZoneRef.current) {
      pendingNoticeRef.current = 'now';
      if (source === 'manager' && !isInitiator) {
        notifications.show(REFRESH_MANAGER_NOW_TOAST);
      }
    } else {
      pendingNoticeRef.current = 'deferred';
      if (!isInitiator) {
        notifications.show(source === 'manager' ? REFRESH_MANAGER_DEFERRED_TOAST : REFRESH_SELF_DEFERRED_TOAST);
      }
    }
    armSettle();
  }, [armSettle]);

  // On any safe/unsafe flip: arm the countdown when safe, cancel it when the zone turns unsafe.
  useEffect(() => {
    if (isSafeZone) {
      armSettle();
    } else {
      clearSettle();
    }
  }, [isSafeZone, armSettle, clearSettle]);

  useEffect(() => clearSettle, [clearSettle]);

  // Shows the surviving post-reload notice once on mount, then clears it.
  useEffect(() => {
    let notice: RefreshNotice | null = null;
    try {
      notice = sessionStorage.getItem(REFRESH_NOTICE_KEY) as RefreshNotice | null;
      if (notice) {
        sessionStorage.removeItem(REFRESH_NOTICE_KEY);
      }
    } catch {
      // sessionStorage may be unavailable; skip the post-reload notice in that case.
    }
    if (!notice) {
      return;
    }
    notifications.show(consumeInitiatorMarker(true) ? REFRESH_BROADCAST_DONE_TOAST : REFRESH_DONE_TOAST);
  }, []);

  return { requestRefresh };
}
